import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Stack,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tabs,
    Tab,
    Card,
    CardContent,
    Tooltip,
    IconButton,
    Collapse,
    Divider,
} from '@mui/material';
import {
    MergeType as MergeTypeIcon,
    Close as CloseIcon,
    OpenInNew as OpenInNewIcon,
    SportsEsports as SportsEsportsIcon,
    CheckCircle as CheckCircleIcon,
    Autorenew as AutorenewIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Add as AddIcon,
    ListAlt as ListAltIcon,
} from '@mui/icons-material';
import {
    getPendingImports,
    confirmPendingImport,
    confirmBatchPendingImport,
    mergePendingImport,
    dismissPendingImport,
    getDuelsSyncStatus,
    retryAutoMatch,
} from '../../services/duels-sync-service';
import { getUserDecks, getInternalDecks, getAllArchetypes } from '../../services/tracker-data';
import InkColorIcon from './InkColorIcon';

const INK_COLOR_NAMES = {
    amber: 'Amber', amethyst: 'Amethyst', emerald: 'Emerald',
    ruby: 'Ruby', sapphire: 'Sapphire', steel: 'Steel',
};

// ── Narzędzia diff kart ─────────────────────────────────────────────────────
const buildCardMap = (text) => {
    const map = new Map();
    if (!text) return map;
    for (const line of text.split(/\r?\n/)) {
        const m = line.trim().match(/^(\d+)\s+(.+)$/);
        if (!m) continue;
        map.set(m[2].trim().toLowerCase(), (map.get(m[2].trim().toLowerCase()) || 0) + parseInt(m[1], 10));
    }
    return map;
};

const computeDeckDiff = (raw1, raw2) => {
    if (!raw1 || !raw2) return null;
    const a = buildCardMap(raw1);
    const b = buildCardMap(raw2);
    const names = new Set([...a.keys(), ...b.keys()]);
    let diff = 0;
    for (const n of names) diff += Math.abs((a.get(n) || 0) - (b.get(n) || 0));
    return diff;
};

// ── Pomocniki ───────────────────────────────────────────────────────────────
function ColorChips({ colors }) {
    if (!colors?.length) return <Typography variant="caption" color="text.secondary">?</Typography>;
    return (
        <Stack direction="row" spacing={0.5}>
            {colors.map((c) => (
                <Chip
                    key={c}
                    size="small"
                    icon={<InkColorIcon color={c} size={14} tooltip={false} />}
                    label={INK_COLOR_NAMES[c.toLowerCase()] || c}
                    sx={{ height: 20, fontSize: '0.65rem', px: 0.25 }}
                />
            ))}
        </Stack>
    );
}

function ResultChip({ result }) {
    return (
        <Chip
            size="small"
            label={result === 'win' ? 'Wygrana' : 'Przegrana'}
            sx={{
                height: 20, fontSize: '0.65rem', fontWeight: 700,
                backgroundColor: result === 'win' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                color: result === 'win' ? '#4ade80' : '#f87171',
            }}
        />
    );
}

function formatDate(ts) {
    if (!ts) return '—';
    // Neon zwraca BIGINT jako string cyfr — konwertuj przez Number zanim przekażesz do Date
    const num = Number(ts);
    const d = !isNaN(num) && num > 0 ? new Date(num) : new Date(ts);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('pl-PL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/* ── Opcja talii w selektorze ── */
function DeckOption({ deck, archetype, diffCount }) {
    const colors = archetype?.ink_colors || [];
    return (
        <Stack direction="row" spacing={0.75} alignItems="center">
            {colors.length > 0 && (
                <Stack direction="row" spacing={0.25}>
                    {colors.map(c => <InkColorIcon key={c} color={c} size={14} tooltip={false} />)}
                </Stack>
            )}
            {archetype?.name && (
                <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 600, fontSize: '0.75rem' }}>
                    {archetype.name}
                </Typography>
            )}
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                {deck.name || deck.id?.slice(0, 8)}
            </Typography>
            {diffCount !== null && diffCount !== undefined && (
                <Chip
                    size="small"
                    label={diffCount === 0 ? 'idealne' : `diff: ${diffCount}`}
                    sx={{
                        ml: 'auto', height: 16, fontSize: '0.6rem',
                        backgroundColor: diffCount === 0 ? 'rgba(34,197,94,0.2)' : diffCount <= 3 ? 'rgba(251,191,36,0.2)' : 'rgba(148,163,184,0.15)',
                        color: diffCount === 0 ? '#4ade80' : diffCount <= 3 ? '#fbbf24' : '#94a3b8',
                    }}
                />
            )}
        </Stack>
    );
}

const MAX_DIFF_SHOW = 32;

// ── Filtrowanie talii dla grupy ─────────────────────────────────────────────
function filterDecksForGroup(decks, archetypes, yourDeckColors, importedCardsRaw) {
    const hasColors = yourDeckColors?.length > 0;
    const result = [];
    for (const deck of decks) {
        const arch = deck.archetype
            ? (archetypes.find(a => a.id === deck.archetype) || null)
            : null;
        const archColors = arch?.ink_colors || [];

        // Filtruj po kolorach TYLKO gdy:
        // - wiemy jakie kolory ma importowana talia (hasColors)
        // - ORAZ talia ma archetyp z kolorami (znamy jej kolory)
        if (hasColors && archColors.length > 0) {
            const ac = archColors.map(c => c.toLowerCase());
            const yc = yourDeckColors.map(c => c.toLowerCase());
            // Wyklucz jeśli którykolwiek z wykrytych kolorów nie należy do archetypu.
            // Subset check (nie exact-match) obsługuje przypadek niekompletnego wykrycia kolorów.
            if (!yc.every(c => ac.includes(c))) continue;
        }
        // Jeśli hasColors ale talia nie ma archetypu/kolorów → zostaw (nie możemy zweryfikować)

        const diffCount = computeDeckDiff(importedCardsRaw, deck.cards || '');
        if (diffCount !== null && diffCount > MAX_DIFF_SHOW) continue;
        result.push({ deck, archetype: arch, diffCount });
    }
    return result.sort((a, b) => (a.diffCount ?? 999) - (b.diffCount ?? 999));
}

/* ─────────────────────── GROUPED DECK ASSIGNMENT ──────────────────────────── */
function GroupedDeckAssignment({ items, decks, archetypes, onDone, onAddDeck }) {
    const first = items[0] || {};
    const importedCardsRaw = first.importedCardsRaw || null;
    const yourDeckColors = first.yourDeckColors || [];
    const [selectedDeckId, setSelectedDeckId] = useState(first.suggestedDeck?.id || '');
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [showCards, setShowCards] = useState(false);

    const filteredDecks = useMemo(
        () => filterDecksForGroup(decks, archetypes, yourDeckColors, importedCardsRaw),
        [decks, archetypes, yourDeckColors, importedCardsRaw]
    );

    const selectedDeckEntry = filteredDecks.find(e => e.deck.id === selectedDeckId);

    const handleImportGroup = async () => {
        setBusy(true);
        try {
            const ids = items.map(i => i.id);
            await confirmBatchPendingImport(ids, selectedDeckId || null);
            ids.forEach(id => onDone(id));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card
            variant="outlined"
            sx={{
                backgroundColor: 'rgba(255,255,255,0.03)',
                borderColor: 'rgba(124, 58, 237, 0.25)',
                borderRadius: 2,
                mb: 2,
            }}
        >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                {/* Nagłówek grupy */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    {yourDeckColors.length > 0 ? (
                        <Stack direction="row" spacing={0.3}>
                            {yourDeckColors.map(c => <InkColorIcon key={c} color={c} size={18} />)}
                        </Stack>
                    ) : (
                        <SportsEsportsIcon sx={{ color: '#a78bfa', fontSize: 18 }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', flex: 1 }}>
                        {items.length} mecz{items.length === 1 ? '' : items.length < 5 ? 'e' : 'y'} tą samą talią
                    </Typography>
                    {importedCardsRaw && (
                        <Tooltip title={showCards ? 'Ukryj karty talii' : 'Podgląd kart talii'}>
                            <IconButton size="small" onClick={() => setShowCards(v => !v)} sx={{ color: showCards ? '#a78bfa' : 'text.secondary', p: 0.5 }}>
                                <ListAltIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton size="small" onClick={() => setExpanded(e => !e)} sx={{ color: '#a78bfa', p: 0.5 }}>
                        {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                </Stack>

                {/* Podgląd kart importowanej talii */}
                <Collapse in={showCards}>
                    <Box
                        sx={{
                            mb: 1.5,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: 'rgba(0,0,0,0.25)',
                            border: '1px solid rgba(124,58,237,0.15)',
                            maxHeight: 200,
                            overflowY: 'auto',
                        }}
                    >
                        <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Lista kart z duels.ink
                        </Typography>
                        {(importedCardsRaw || '').split(/\r?\n/).filter(Boolean).map((line, i) => (
                            <Typography key={i} variant="caption" sx={{ display: 'block', color: '#cbd5e1', fontFamily: 'monospace', lineHeight: 1.6 }}>
                                {line}
                            </Typography>
                        ))}
                    </Box>
                </Collapse>

                {/* Lista gier (zwijana) */}
                <Collapse in={expanded}>
                    <Box sx={{ mb: 1.5, pl: 0.5 }}>
                        {items.map(item => {
                            const gameDate = item.started_at || item.createdAt;
                            return (
                                <Stack key={item.id} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                                    <ResultChip result={item.result} />
                                    <Chip size="small" label={item.playDraw === 'play' ? 'Play' : 'Draw'} sx={{ height: 20, fontSize: '0.65rem' }} />
                                    <Typography variant="caption" color="text.secondary">{formatDate(gameDate)}</Typography>
                                    {item.opponentName && <Typography variant="caption" color="text.secondary">vs <strong>{item.opponentName}</strong></Typography>}
                                    <ColorChips colors={item.opponentColors} />
                                    {item.replayUrl && (
                                        <Tooltip title="Otwórz powtórkę">
                                            <IconButton size="small" component="a" href={item.replayUrl} target="_blank" rel="noopener noreferrer" sx={{ p: 0.25, color: '#a78bfa' }}>
                                                <OpenInNewIcon sx={{ fontSize: 13 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            );
                        })}
                    </Box>
                    <Divider sx={{ mb: 1.5, borderColor: 'rgba(124,58,237,0.15)' }} />
                </Collapse>

                {/* Selektor talii */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel sx={{ fontSize: '0.75rem' }}>Talia</InputLabel>
                        <Select
                            value={selectedDeckId}
                            onChange={(e) => setSelectedDeckId(e.target.value)}
                            label="Talia"
                            sx={{ fontSize: '0.8rem' }}
                            renderValue={(val) => {
                                if (!val) return <em style={{ color: 'rgba(196,181,253,0.5)' }}>Brak talii</em>;
                                const e = filteredDecks.find(x => x.deck.id === val);
                                if (!e) return val;
                                return <DeckOption deck={e.deck} archetype={e.archetype} diffCount={e.diffCount} />;
                            }}
                        >
                            <MenuItem value=""><em>Brak talii</em></MenuItem>
                            {filteredDecks.length === 0 && (
                                <MenuItem disabled>
                                    <Typography variant="caption" color="text.secondary">
                                        {yourDeckColors.length > 0
                                            ? `Brak talii o kolorach ${yourDeckColors.join('/')} z diff ≤ ${MAX_DIFF_SHOW} — dodaj talię`
                                            : 'Brak pasujących talii — dodaj talię'}
                                    </Typography>
                                </MenuItem>
                            )}
                            {filteredDecks.map(({ deck, archetype, diffCount }) => (
                                <MenuItem key={deck.id} value={deck.id}>
                                    <DeckOption deck={deck} archetype={archetype} diffCount={diffCount} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleImportGroup}
                        disabled={busy}
                        startIcon={busy ? <CircularProgress size={12} /> : <CheckCircleIcon />}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: 'rgba(124,58,237,0.8)',
                            '&:hover': { backgroundColor: '#7c3aed' },
                            whiteSpace: 'nowrap',
                            fontSize: '0.75rem',
                        }}
                    >
                        Importuj {items.length > 1 ? `(${items.length})` : ''}
                    </Button>

                    {onAddDeck && (
                        <Tooltip title="Dodaj nową talię z tą listą kart">
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onAddDeck(importedCardsRaw)}
                                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.7rem',
                                    borderColor: 'rgba(124,58,237,0.4)',
                                    color: '#a78bfa',
                                    whiteSpace: 'nowrap',
                                    '&:hover': { borderColor: '#7c3aed', backgroundColor: 'rgba(124,58,237,0.1)' },
                                }}
                            >
                                Dodaj talię
                            </Button>
                        </Tooltip>
                    )}

                    <Tooltip title="Pomiń tę grupę">
                        <IconButton
                            size="small"
                            disabled={busy}
                            onClick={async () => {
                                setBusy(true);
                                try {
                                    await Promise.all(items.map(i => dismissPendingImport(i.id)));
                                    items.forEach(i => onDone(i.id));
                                } finally { setBusy(false); }
                            }}
                            sx={{ color: '#f87171' }}
                        >
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </CardContent>
        </Card>
    );
}

/* ─────────────────────── POTENTIAL DUPLICATE ITEM ─────────────────────── */
function PotentialDuplicateItem({ item, onDone }) {
    const [busy, setBusy] = useState(null);

    const act = async (action) => {
        setBusy(action);
        try {
            if (action === 'merge') await mergePendingImport(item.id);
            else if (action === 'keep') await confirmPendingImport(item.id, null);
            else await dismissPendingImport(item.id);
            onDone(item.id);
        } finally { setBusy(null); }
    };

    return (
        <Card
            variant="outlined"
            sx={{ backgroundColor: 'rgba(59,130,246,0.04)', borderColor: 'rgba(59,130,246,0.25)', borderRadius: 2, mb: 1.5 }}
        >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
                    <Box sx={{ flex: 1, p: 1, borderRadius: 1, backgroundColor: 'rgba(124,58,237,0.07)' }}>
                        <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 700, display: 'block', mb: 0.5 }}>duels.ink</Typography>
                        <Stack spacing={0.4}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <ResultChip result={item.result} />
                                <Chip size="small" label={item.playDraw === 'play' ? 'Play' : 'Draw'} sx={{ height: 20, fontSize: '0.65rem' }} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">{formatDate(item.started_at)}</Typography>
                            {item.opponentName && <Typography variant="caption" color="text.secondary">vs {item.opponentName}</Typography>}
                            <ColorChips colors={item.opponentColors} />
                            {item.replayUrl && (
                                <Button size="small" component="a" href={item.replayUrl} target="_blank" rel="noopener noreferrer"
                                    endIcon={<OpenInNewIcon sx={{ fontSize: 11 }} />}
                                    sx={{ textTransform: 'none', fontSize: '0.65rem', py: 0, px: 0.5, justifyContent: 'flex-start', color: '#a78bfa' }}>
                                    Powtórka
                                </Button>
                            )}
                        </Stack>
                    </Box>
                    <Box sx={{ flex: 1, p: 1, borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.04)' }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, display: 'block', mb: 0.5 }}>Wpis ręczny</Typography>
                        <Stack spacing={0.4}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <ResultChip result={item.result} />
                                <Chip size="small" label={item.playDraw === 'play' ? 'Play' : 'Draw'} sx={{ height: 20, fontSize: '0.65rem' }} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">~{formatDate(item.matchedMatchTs)}</Typography>
                            {item.matchedOpponentArchetypeId
                                ? <Typography variant="caption" sx={{ color: '#a78bfa' }}>Archetyp ustawiony ✓</Typography>
                                : <Typography variant="caption" color="text.secondary">Brak archetypu</Typography>
                            }
                            {item.colorsMatch && (
                                <Chip size="small" label="Kolory zgodne ✓" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(34,197,94,0.1)', color: '#4ade80', alignSelf: 'flex-start' }} />
                            )}
                        </Stack>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Tooltip title="Zachowaj wpis ręczny i wzbogać go o dane powtórki z duels.ink">
                        <Button size="small" variant="contained" onClick={() => act('merge')} disabled={Boolean(busy)}
                            startIcon={busy === 'merge' ? <CircularProgress size={12} /> : <MergeTypeIcon />}
                            sx={{ textTransform: 'none', backgroundColor: 'rgba(34,197,94,0.2)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)', '&:hover': { backgroundColor: 'rgba(34,197,94,0.3)' }, fontSize: '0.75rem' }}>
                            Scal
                        </Button>
                    </Tooltip>
                    <Tooltip title="Importuj jako osobny wpis (inna gra)">
                        <Button size="small" variant="outlined" onClick={() => act('keep')} disabled={Boolean(busy)}
                            startIcon={busy === 'keep' ? <CircularProgress size={12} /> : <SportsEsportsIcon />}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', borderColor: 'rgba(148,163,184,0.3)', color: '#94a3b8' }}>
                            Inna gra
                        </Button>
                    </Tooltip>
                    <Tooltip title="Zachowaj tylko wpis ręczny, pomiń import">
                        <Button size="small" variant="text" onClick={() => act('skip')} disabled={Boolean(busy)}
                            startIcon={busy === 'skip' ? <CircularProgress size={12} /> : <CloseIcon />}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#64748b' }}>
                            Pomiń import
                        </Button>
                    </Tooltip>
                </Stack>
            </CardContent>
        </Card>
    );
}

/* ─────────────────────── MAIN COMPONENT ─────────────────────── */
function DuelSyncPendingQueue({ onStatsUpdate, trackerFilters, onAddDeck }) {
    const [tab, setTab] = useState('deck_assignment');
    const [items, setItems] = useState([]);
    const [decks, setDecks] = useState([]);
    const [archetypes, setArchetypes] = useState([]);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState(false);
    const [retryResult, setRetryResult] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [statusData, myDecksData, internalDecksData, archetypesData, pendingData] = await Promise.all([
                getDuelsSyncStatus(),
                getUserDecks(undefined, {}),   // własne talie — bez filtra formatu/setu
                getInternalDecks({}),          // talie wewnętrzne/publiczne z całego systemu
                getAllArchetypes(),             // wszystkie archetypy — do lookup kolorów
                getPendingImports(),
            ]);
            setStatus(statusData);
            // Połącz własne + systemowe, deduplikuj po ID
            const seen = new Set();
            const allDecks = [];
            for (const d of [...(myDecksData || []), ...(internalDecksData || [])]) {
                if (!seen.has(d.id)) { seen.add(d.id); allDecks.push(d); }
            }
            setDecks(allDecks);
            setArchetypes(archetypesData || []);
            setItems(pendingData || []);
        } catch {
            setError('Błąd ładowania danych. Odśwież stronę.');
        } finally {
            setLoading(false);
        }
    }, [trackerFilters]);

    useEffect(() => { load(); }, [load]);

    const handleDone = useCallback((id) => {
        setItems(prev => prev.filter(i => i.id !== id));
        if (onStatsUpdate) onStatsUpdate();
    }, [onStatsUpdate]);

    const handleRetryAutoMatch = async () => {
        setRetrying(true);
        setRetryResult(null);
        try {
            const result = await retryAutoMatch();
            setRetryResult(result);
            if (result.imported > 0) { await load(); if (onStatsUpdate) onStatsUpdate(); }
        } catch {
            setRetryResult({ error: true });
        } finally { setRetrying(false); }
    };

    const deckAssignments = items.filter(i => i.type === 'deck_assignment');
    const potentialDuplicates = items.filter(i => i.type === 'potential_duplicate');

    // Grupuj przypisania talii wg duelsDeckId (ta sama lista kart)
    const deckGroups = useMemo(() => {
        const groups = new Map();
        for (const item of deckAssignments) {
            const key = item.duelsDeckId || item.id; // fallback: pojedynczy element
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(item);
        }
        return [...groups.values()];
    }, [deckAssignments]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">Ładowanie...</Typography>
            </Box>
        );
    }

    if (error) return <Alert severity="error">{error}</Alert>;

    if (!status?.connected) {
        return <Alert severity="info">Połącz konto duels.ink w <strong>Ustawienia → duels.ink</strong>.</Alert>;
    }

    if (deckAssignments.length === 0 && potentialDuplicates.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 48, color: '#4ade80', mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 0.5 }}>Wszystko gotowe</Typography>
                <Typography variant="body2" color="text.secondary">Brak gier oczekujących na weryfikację.</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 2, '& .MuiTabs-indicator': { backgroundColor: '#7c3aed' }, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
            >
                <Tab
                    value="deck_assignment"
                    label={
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <span>Przypisanie talii</span>
                            {deckAssignments.length > 0 && (
                                <Chip size="small" label={deckAssignments.length} sx={{ height: 18, fontSize: '0.65rem', backgroundColor: 'rgba(251,191,36,0.2)', color: '#fbbf24' }} />
                            )}
                        </Stack>
                    }
                    sx={{ textTransform: 'none', minHeight: 40, '&.Mui-selected': { color: '#c4b5fd' } }}
                />
                <Tab
                    value="potential_duplicate"
                    label={
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <span>Możliwe duplikaty</span>
                            {potentialDuplicates.length > 0 && (
                                <Chip size="small" label={potentialDuplicates.length} sx={{ height: 18, fontSize: '0.65rem', backgroundColor: 'rgba(59,130,246,0.2)', color: '#60a5fa' }} />
                            )}
                        </Stack>
                    }
                    sx={{ textTransform: 'none', minHeight: 40, '&.Mui-selected': { color: '#c4b5fd' } }}
                />
            </Tabs>

            {tab === 'deck_assignment' && (
                <Box>
                    {deckGroups.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Brak gier oczekujących na przypisanie talii.</Typography>
                    ) : (
                        <>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pr: 1 }}>
                                    Te gry zostały zaimportowane, ale nie znaleziono pasującej talii. Gry z tą samą listą kart są pogrupowane — wybierz talię dla całej grupy jednym kliknięciem.
                                </Typography>
                                <Tooltip title="Sprawdź ponownie wszystkie oczekujące gry względem twoich aktualnych talii. Nowo dodane talie mogą teraz pasować automatycznie.">
                                    <Button
                                        size="small" variant="outlined" onClick={handleRetryAutoMatch} disabled={retrying}
                                        startIcon={retrying ? <CircularProgress size={12} /> : <AutorenewIcon />}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem', flexShrink: 0, ml: 1, borderColor: 'rgba(124,58,237,0.5)', color: '#a78bfa' }}
                                    >
                                        Ponów auto-dopasowanie
                                    </Button>
                                </Tooltip>
                            </Stack>

                            {retryResult && !retryResult.error && (
                                <Alert severity={retryResult.imported > 0 ? 'success' : 'info'} sx={{ mb: 1.5, py: 0.5 }} onClose={() => setRetryResult(null)}>
                                    {retryResult.imported > 0
                                        ? `Automatycznie zaimportowano ${retryResult.imported} gr${retryResult.imported === 1 ? 'ę' : 'y'}. ${retryResult.stillPending} wymaga ręcznego przeglądu.`
                                        : `Brak nowych dopasowań. ${retryResult.stillPending} gr${retryResult.stillPending === 1 ? 'a' : 'y'} nadal wymaga ręcznego przypisania talii.`}
                                </Alert>
                            )}

                            {deckGroups.map((group, i) => (
                                <GroupedDeckAssignment
                                    key={group[0]?.duelsDeckId || i}
                                    items={group}
                                    decks={decks}
                                    archetypes={archetypes}
                                    onDone={handleDone}
                                    onAddDeck={onAddDeck}
                                />
                            ))}
                        </>
                    )}
                </Box>
            )}

            {tab === 'potential_duplicate' && (
                <Box>
                    {potentialDuplicates.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Brak możliwych duplikatów.</Typography>
                    ) : (
                        <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                Te gry z duels.ink mogą być duplikatami ręcznie wpisanych meczy (ta sama data ±2h, wynik i kolejność Play/Draw).
                            </Typography>
                            {potentialDuplicates.map(item => (
                                <PotentialDuplicateItem key={item.id} item={item} onDone={handleDone} />
                            ))}
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default DuelSyncPendingQueue;
